import {
  Task,
  taskCountRespZod,
  taskZod,
} from 'packages/lib/api/annotations/contract/task';
import { apiGet } from 'packages/lib/api/annotations/rest';
import { range } from 'lodash';
import z from 'zod';
import useSWR from 'swr';
import { SampleGroup } from 'packages/lib/api/annotations/contract/sample-group';
import { dispatchErrorToast } from 'packages/lib/common-ui/toast/api-toast';
import { taskGroupsRespZod } from 'packages/lib/api/annotations/contract/task-group';
import {
  AnnotationTool,
  taskTypeToWorkflowAlias,
} from 'packages/task-man/constants';

/* Constants that govern lookup parameters */
const PAGE_SIZE = 2000;
const REFRESH_RATE_MS = 0; // disabled

/* Interface for making buildable query strings (in the future) */
interface TaskLookupQuery {
  partner: string;
  assignee: string;
  annotationState: string;
}

/**
 * Utility function that fetches tasks given a set of pages and task lookup query.
 * @param params a valid instruction token, task lookup query, url, and set of page.
 * @returns A multi-dimensional Task array that should be flattened by the caller of this fuction.
 */
const fetchTasks = ({
  instructionToken,
  query,
  url,
  pages,
}: {
  instructionToken: string;
  query: TaskLookupQuery;
  url: string;
  pages: number[];
}): Promise<Task[][]> => {
  return Promise.all(
    pages.map((page) =>
      apiGet<Task[]>(
        `${url}?instructionToken=${instructionToken}&annotationState=${query.annotationState}&assignee=${query.assignee}&partner=${query.partner}&pageSize=${PAGE_SIZE}&pageNo=${page}`,
        z.array(taskZod).parse
      )
    )
  );
};

/**
 * A hook to get a list of tasks given an instruction token and task lookup query parameters.
 * @param instructionToken A valid instruction token (if null/undefined, an empty array will return)
 * @param query TaskLookupQuery parameters such as assignee, annotationState, partner, etc.
 * @returns A malformed, custom SWRResponse with a task list, error (if any), and isLoading.
 */
export function useTasksForInstruction(
  instructionToken: string | null | undefined,
  query: TaskLookupQuery
): { tasks: Task[] | undefined; error: unknown; isLoading: boolean } {
  // Important to note that if there is no instructionToken (i.e. null/undefined), this API call won't run.
  const { data: taskCount } = useSWR(
    instructionToken
      ? `/tasks/count?instructionToken=${instructionToken}&annotationState=${query.annotationState}&assignee=${query.assignee}&partner=${query.partner}`
      : undefined,
    (url: string) => apiGet(url, taskCountRespZod.parse)
  );

  // Simple math to get the maximum number of API calls/pages we'll have to sift through.
  const maxPage = taskCount ? Math.ceil(taskCount['*'] / PAGE_SIZE) : 0;

  // Important to note that if taskCount isn't updated, or null/undefined from the instructionToken,
  // this SWR call won't go through either.
  const { data, error, isLoading } = useSWR<Task[][]>(
    taskCount
      ? {
          url: '/tasks',
          pages: range(1, maxPage + 1),
          instructionToken,
          query,
        }
      : undefined,
    fetchTasks,
    {
      refreshInterval: REFRESH_RATE_MS,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const tasks = data ? data.flat() : undefined;
  return { tasks, error, isLoading };
}

export const useTaskGroupsForSampleGroups = (
  sampleGroups: SampleGroup[] | null | undefined,
  workflow: AnnotationTool
) =>
  useSWR(
    sampleGroups
      ? `/task-groups/stats?partner=None&sampleGroupToken=${sampleGroups
          .map((s) => s.token)
          .join(',')}`
      : undefined,
    (url) =>
      apiGet(url, taskGroupsRespZod.parse)
        .then((tgs) =>
          tgs.filter((tg) => taskTypeToWorkflowAlias[tg.type] === workflow)
        )
        .catch((err) => {
          dispatchErrorToast(
            `Failed to fetch TaskGroup data for given set of SampleGroup tokens. Please report this error and include browser console outputs.`
          );
          console.log(
            `Failed to fetch TaskGroups for SampleGroups: ${url}`,
            err
          );
          throw err;
        })
  );
